import {
  createTheme,
  type PaletteOptions,
  type ThemeOptions
} from "@mui/material/styles";

import { autostoreDesignSystemColors as ds } from "./autostoreDesignSystem";
import { autostoreTheme } from "./theme";

const designSystemPalette: Partial<PaletteOptions> = {
  primary: {
    main: ds.surface.interactive.accent.default,
    dark: ds.surface.interactive.accent.hover,
    contrastText: ds.text.onInverse.primary
  },
  divider: ds.border.onStandard.surfaceDefault,
  text: {
    primary: ds.text.onStandard.primary,
    secondary: ds.text.onStandard.secondary
  },
  border: {
    main: ds.border.onStandard.surfaceDefault
  },
  gray: {
    main: autostoreTheme.palette.gray.main,
    light: ds.surface.static.alternate
  },
  ambient: {
    main: ds.colour.tertiary.asYellow300
  },
  chilled: {
    main: ds.colour.secondary.asBlue300
  },
  frozen: {
    main: ds.colour.tertiary.asPurple300
  },
  tableRowSelected: {
    main: ds.border.onStandard.statusInformative,
    light: ds.surface.static.informative
  },
  toastFuseColor: {
    main: "rgba(0, 0, 0, 0.2)"
  },
  toastFuseUnderlay: {
    success: ds.border.onStandard.statusPositive,
    warning: ds.border.onStandard.statusWarning,
    info: ds.border.onStandard.statusInformative,
    error: ds.border.onStandard.statusNegative
  },
  productCardSafety: {
    warning: ds.icon.onStandard.statusWarning,
    sanitize: ds.mts.organic
  },
  productCard: {
    band: ds.surface.interactive.muted.disabled
  },
  binAnimation: {
    overlay: ds.colour.primary.asWarmGray600
  },
  toteText: {
    primary: ds.text.onStandard.primary
  },
  binCompartment: {
    openQuantity: ds.text.onStandard.primary,
    selectedBorder: ds.border.onStandard.statusInformative
  }
};

const alertSeverityStyles = {
  success: {
    backgroundColor: ds.surface.static.positive,
    border: `2px solid ${ds.border.onStandard.statusPositive}`,
    color: ds.text.onStandard.primary,
    "& .MuiAlert-icon": {
      color: ds.icon.onStandard.statusPositive
    }
  },
  warning: {
    backgroundColor: ds.surface.static.warning,
    border: `2px solid ${ds.border.onStandard.statusWarning}`,
    color: ds.text.onStandard.primary,
    "& .MuiAlert-icon": {
      color: ds.icon.onStandard.statusWarning
    }
  },
  info: {
    backgroundColor: ds.surface.static.informative,
    border: `2px solid ${ds.border.onStandard.statusInformative}`,
    color: ds.text.onStandard.primary,
    "& .MuiAlert-icon": {
      color: ds.icon.onStandard.statusInformative
    }
  },
  error: {
    backgroundColor: ds.surface.static.negative,
    border: `2px solid ${ds.border.onStandard.statusNegative}`,
    color: ds.text.onStandard.primary,
    "& .MuiAlert-icon": {
      color: ds.icon.onStandard.statusNegative
    }
  }
} as const;

const standardAlertSeverity = (severity: keyof typeof alertSeverityStyles) => ({
  "&&": {
    ...alertSeverityStyles[severity],
    "& .MuiTypography-root": {
      color: ds.text.onStandard.primary
    }
  }
});

const standardAlertStyles = {
  success: standardAlertSeverity("success"),
  warning: standardAlertSeverity("warning"),
  info: standardAlertSeverity("info"),
  error: standardAlertSeverity("error")
} as const;

const designSystemComponents: ThemeOptions["components"] = {
  MuiButton: {
    styleOverrides: {
      containedPrimary: {
        "&.MuiButton-containedPrimary.Mui-disabled": {
          background: ds.surface.interactive.accent.disabled,
          color: ds.text.onInverse.primary
        }
      },
      containedSecondary: {
        backgroundColor: ds.surface.interactive.muted.default,
        color: ds.text.onStandard.primary,
        "&:hover": {
          backgroundColor: ds.surface.interactive.muted.hover
        },
        "&.Mui-disabled": {
          backgroundColor: ds.surface.interactive.muted.disabled,
          color: ds.text.onStandard.disabled
        }
      }
    }
  },
  MuiCard: {
    styleOverrides: {
      root: {
        backgroundColor: ds.surface.static.default,
        borderColor: ds.border.onStandard.surfaceDefault,
        color: ds.text.onStandard.primary
      }
    }
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        "&.Mui-focused:not(.Mui-error)": {
          color: ds.text.onStandard.primary
        }
      }
    }
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: ({ ownerState, theme }) => ({
        ...(ownerState.error && {
          backgroundColor: theme.palette.error.light
        }),
        "&:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
          borderColor: ds.border.onStandard.inputDefault
        },
        "&:hover:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
          borderColor: ds.border.onStandard.inputHover
        },
        "&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
          borderColor: ds.border.onStandard.inputDefault
        }
      })
    }
  },
  MuiAlert: {
    styleOverrides: {
      filledSuccess: alertSeverityStyles.success,
      filledWarning: alertSeverityStyles.warning,
      filledInfo: alertSeverityStyles.info,
      filledError: alertSeverityStyles.error,
      standardSuccess: standardAlertStyles.success,
      standardWarning: standardAlertStyles.warning,
      standardInfo: standardAlertStyles.info,
      standardError: standardAlertStyles.error,
      action: {
        color: "inherit"
      }
    }
  },
  MuiTable: {
    styleOverrides: {
      root: {
        background: ds.surface.static.alternate
      }
    }
  },
  MuiTableCell: {
    styleOverrides: {
      body: {
        background: ds.surface.static.default
      },
      head: {
        backgroundColor: ds.surface.static.alternate,
        color: ds.text.onStandard.secondary
      }
    }
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        "&.Mui-selected > td": {
          background: ds.surface.static.informative,
          borderColor: ds.border.onStandard.statusInformative
        }
      }
    }
  }
};

export const designSystemTheme = createTheme(autostoreTheme, {
  palette: designSystemPalette,
  components: designSystemComponents
});
